// Per-section editor. One card = one RoofSection. Every field is editable
// and the values persist directly to the parent quote. All measurements
// are EAVE-TO-RIDGE based, never plan-view width.
import React, { useState } from "react";
import { Trash, Plus, Sparkle, CaretDown, CaretRight, Warning } from "@phosphor-icons/react";
import { ROOF_TYPES } from "../../lib/roofTypes";
import {
  COVERAGE_OPTIONS,
  COVERING_CATEGORIES,
  FEATURE_TYPES,
  WIND_ZONES,
  EXPOSURE_OPTIONS,
  newFeature,
  newCovering,
} from "../../lib/roofPresets";

const num = (v) => (v === "" || v == null ? "" : Number(v));

function Field({ label, hint, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="overline">{label}</span>
      {children}
      {hint && <div className="text-[10px] text-slate-500 mt-1">{hint}</div>}
    </label>
  );
}

export default function SectionEditor({ section, index, onChange, onRemove, perSectionCalc, sectionWarnings = [] }) {
  const [open, setOpen] = useState(index === 0);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const set = (k, v) => onChange({ ...section, [k]: v });
  const setNum = (k, v) => onChange({ ...section, [k]: v === "" ? "" : Number(v) });

  const updateFeature = (idx, k, v) => {
    const next = section.features.map((f, i) => (i === idx ? { ...f, [k]: k === "count" ? Number(v || 0) : (["width", "height", "length", "area", "unit_cost", "labour_hours", "ai_confidence"].includes(k) ? (v === "" ? null : Number(v)) : v) } : f));
    onChange({ ...section, features: next });
  };
  const removeFeature = (idx) => onChange({ ...section, features: section.features.filter((_, i) => i !== idx) });
  const addFeature = () => onChange({ ...section, features: [...(section.features || []), newFeature("valley")] });

  const updateCovering = (idx, k, v) => {
    const next = (section.coverings || []).map((c, i) => (i === idx ? { ...c, [k]: k === "area_pct" || k === "waste_pct_override" ? (v === "" ? null : Number(v)) : v } : c));
    onChange({ ...section, coverings: next });
  };
  const removeCovering = (idx) => onChange({ ...section, coverings: section.coverings.filter((_, i) => i !== idx) });
  const addCovering = () => onChange({ ...section, coverings: [...(section.coverings || []), newCovering()] });

  const area = perSectionCalc?.roof_area_m2 ?? 0;
  const battens = perSectionCalc?.battens_m ?? 0;
  const ridge = perSectionCalc?.ridge_length_m ?? 0;
  const valley = perSectionCalc?.valley_length_m ?? 0;
  const hip = perSectionCalc?.hip_length_m ?? 0;

  const critWarns = sectionWarnings.filter((w) => w.severity === "critical").length;
  const warnWarns = sectionWarnings.filter((w) => w.severity === "warning").length;

  return (
    <div className="border border-slate-200 rounded-sm bg-white" data-testid={`section-card-${index}`}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen(!open)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(!open); } }}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-50 cursor-pointer select-none"
        data-testid={`section-toggle-${index}`}
      >
        {open ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-sm truncate">
            {section.name || `Section ${index + 1}`}
            {section.ai_generated && (
              <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-violet-700 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded-sm">
                <Sparkle size={10} weight="fill" /> AI {section.ai_confidence ? `${Math.round(section.ai_confidence * 100)}%` : ""}
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 font-mono-data">
            {ROOF_TYPES.find((r) => r.id === section.roof_type)?.name || section.roof_type} · {Number(section.length || 0).toFixed(2)}m × {Number(section.eave_to_ridge || 0).toFixed(2)}m E2R · {section.pitch}° · {section.slope_coverage}
          </div>
        </div>
        <div className="text-right font-mono-data text-xs">
          <div className="font-bold">{area.toFixed(1)} m²</div>
          <div className="text-slate-500 text-[10px]">area</div>
        </div>
        {(critWarns + warnWarns) > 0 && (
          <span
            className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${critWarns ? "text-red-700 bg-red-50 border-red-200" : "text-amber-700 bg-amber-50 border-amber-200"}`}
            data-testid={`section-warnings-${index}`}
          >
            <Warning size={10} weight="fill" /> {critWarns + warnWarns}
          </span>
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="btn-secondary !px-2 !py-1 text-red-600 ml-2"
          data-testid={`section-remove-${index}`}
          title="Remove section"
        >
          <Trash size={12} />
        </button>
      </div>

      {open && (
        <div className="p-4 border-t border-slate-200 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Section name" testid={`field-name-${index}`}>
              <input className="rqp mt-1.5" value={section.name || ""} onChange={(e) => set("name", e.target.value)} data-testid={`section-name-${index}`} />
            </Field>
            <Field label="Roof type">
              <select className="rqp mt-1.5" value={section.roof_type} onChange={(e) => set("roof_type", e.target.value)} data-testid={`section-roof-type-${index}`}>
                {ROOF_TYPES.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </Field>
            <Field label="Slope coverage" hint="Both = full roof, Front/Rear = single slope only">
              <select className="rqp mt-1.5" value={section.slope_coverage} onChange={(e) => set("slope_coverage", e.target.value)} data-testid={`section-coverage-${index}`}>
                {COVERAGE_OPTIONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Covering category" hint="Resolves BS 5534 batten gauge, headlap & fixing schedule">
              <select className="rqp mt-1.5" value={section.material_category || ""} onChange={(e) => set("material_category", e.target.value || null)} data-testid={`section-cat-${index}`}>
                <option value="">— Set covering —</option>
                {COVERING_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Length (m)" hint="Horizontal eaves length">
              <input type="number" step="0.01" className="rqp mt-1.5" value={section.length ?? ""} onChange={(e) => setNum("length", e.target.value)} data-testid={`section-length-${index}`} />
            </Field>
            <Field label="Eave-to-ridge (m)" hint="Slope (rafter) length — never width">
              <input type="number" step="0.01" className="rqp mt-1.5" value={section.eave_to_ridge ?? ""} onChange={(e) => setNum("eave_to_ridge", e.target.value)} data-testid={`section-etr-${index}`} />
            </Field>
            <Field label="Pitch (°)" hint="0 = flat, 45 = steep">
              <input type="number" step="0.1" className="rqp mt-1.5" value={section.pitch ?? ""} onChange={(e) => setNum("pitch", e.target.value)} data-testid={`section-pitch-${index}`} />
            </Field>
            <Field label="Overhang (m)" hint="Typical 0.30 m">
              <input type="number" step="0.01" className="rqp mt-1.5" value={section.overhang ?? ""} onChange={(e) => setNum("overhang", e.target.value)} data-testid={`section-overhang-${index}`} />
            </Field>
          </div>

          {/* Live per-section calculations */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs font-mono-data pt-3 border-t border-dashed border-slate-200">
            <div className="bg-slate-50 p-2"><div className="text-[10px] uppercase tracking-wider text-slate-500">Area</div><div className="font-bold mt-0.5">{area.toFixed(1)} m²</div></div>
            <div className="bg-slate-50 p-2"><div className="text-[10px] uppercase tracking-wider text-slate-500">Battens</div><div className="font-bold mt-0.5">{battens.toFixed(0)} m</div></div>
            <div className="bg-slate-50 p-2"><div className="text-[10px] uppercase tracking-wider text-slate-500">Ridge</div><div className="font-bold mt-0.5">{ridge.toFixed(1)} m</div></div>
            <div className="bg-slate-50 p-2"><div className="text-[10px] uppercase tracking-wider text-slate-500">Hips</div><div className="font-bold mt-0.5">{hip.toFixed(1)} m</div></div>
            <div className="bg-slate-50 p-2"><div className="text-[10px] uppercase tracking-wider text-slate-500">Valleys</div><div className="font-bold mt-0.5">{valley.toFixed(1)} m</div></div>
          </div>

          {/* Features */}
          <div className="pt-3 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <div className="overline">Features ({section.features?.length || 0})</div>
              <button onClick={addFeature} className="btn-secondary !px-2 !py-1 text-xs flex items-center gap-1" data-testid={`section-add-feature-${index}`}>
                <Plus size={10} weight="bold" /> Add feature
              </button>
            </div>
            <div className="mt-2 space-y-2">
              {(section.features || []).map((f, fi) => (
                <div key={f.id || fi} className="grid grid-cols-12 gap-2 items-center bg-slate-50 p-2 rounded-sm" data-testid={`feature-row-${index}-${fi}`}>
                  <select className="rqp col-span-3 !py-1 text-xs" value={f.type} onChange={(e) => updateFeature(fi, "type", e.target.value)} data-testid={`feature-type-${index}-${fi}`}>
                    {FEATURE_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                  <input className="rqp col-span-2 !py-1 text-xs" placeholder="Label" value={f.label || ""} onChange={(e) => updateFeature(fi, "label", e.target.value)} />
                  <input type="number" className="rqp col-span-1 !py-1 text-xs text-right" placeholder="Qty" value={f.count ?? 1} onChange={(e) => updateFeature(fi, "count", e.target.value)} data-testid={`feature-count-${index}-${fi}`} />
                  <input type="number" step="0.01" className="rqp col-span-2 !py-1 text-xs text-right" placeholder="W (m)" value={num(f.width) || ""} onChange={(e) => updateFeature(fi, "width", e.target.value)} />
                  <input type="number" step="0.01" className="rqp col-span-2 !py-1 text-xs text-right" placeholder="H (m)" value={num(f.height) || ""} onChange={(e) => updateFeature(fi, "height", e.target.value)} />
                  <input type="number" step="0.01" className="rqp col-span-1 !py-1 text-xs text-right" placeholder="L (m)" value={num(f.length) || ""} onChange={(e) => updateFeature(fi, "length", e.target.value)} data-testid={`feature-length-${index}-${fi}`} />
                  <button onClick={() => removeFeature(fi)} className="col-span-1 text-red-600 hover:bg-red-50 rounded-sm py-1" data-testid={`feature-remove-${index}-${fi}`}>
                    <Trash size={12} className="mx-auto" />
                  </button>
                </div>
              ))}
              {(section.features || []).length === 0 && (
                <div className="text-xs text-slate-500 italic">No features. Add valleys, hips, Velux, dormers, chimneys, abutments, flashings, soakers, solar PV…</div>
              )}
            </div>
          </div>

          {/* Coverings (multi) */}
          <div className="pt-3 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <div className="overline">Coverings ({section.coverings?.length || 0})</div>
              <button onClick={addCovering} className="btn-secondary !px-2 !py-1 text-xs flex items-center gap-1" data-testid={`section-add-covering-${index}`}>
                <Plus size={10} weight="bold" /> Add covering
              </button>
            </div>
            <div className="mt-2 space-y-2">
              {(section.coverings || []).map((c, ci) => (
                <div key={c.id || ci} className="grid grid-cols-12 gap-2 items-center bg-slate-50 p-2 rounded-sm" data-testid={`covering-row-${index}-${ci}`}>
                  <input className="rqp col-span-4 !py-1 text-xs" placeholder="Name" value={c.name || ""} onChange={(e) => updateCovering(ci, "name", e.target.value)} />
                  <input className="rqp col-span-3 !py-1 text-xs" placeholder="Manufacturer" value={c.manufacturer || ""} onChange={(e) => updateCovering(ci, "manufacturer", e.target.value)} />
                  <input className="rqp col-span-2 !py-1 text-xs" placeholder="Category" value={c.category || ""} onChange={(e) => updateCovering(ci, "category", e.target.value)} />
                  <input type="number" step="1" className="rqp col-span-2 !py-1 text-xs text-right" placeholder="% area" value={c.area_pct ?? ""} onChange={(e) => updateCovering(ci, "area_pct", e.target.value)} />
                  <button onClick={() => removeCovering(ci)} className="col-span-1 text-red-600 hover:bg-red-50 rounded-sm py-1"><Trash size={12} className="mx-auto" /></button>
                </div>
              ))}
              {(section.coverings || []).length === 0 && (
                <div className="text-xs text-slate-500 italic">Single covering — use the materials table in Step 4. Add more here to mix coverings on this section.</div>
              )}
            </div>
          </div>

          {/* Advanced — unequal pitch + BS compliance context */}
          <div className="pt-3 border-t border-slate-200">
            <button onClick={() => setShowAdvanced(!showAdvanced)} className="text-xs uppercase tracking-wider font-bold text-slate-600 hover:text-[var(--rqp-orange)] flex items-center gap-1" data-testid={`section-advanced-${index}`}>
              {showAdvanced ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
              Advanced — unequal pitch, BS 5534 / 5250 context
            </button>
            {showAdvanced && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                <Field label="Rear pitch (°)" hint="Unequal-pitch only">
                  <input type="number" step="0.1" className="rqp mt-1.5" value={section.rear_pitch ?? ""} onChange={(e) => setNum("rear_pitch", e.target.value)} />
                </Field>
                <Field label="Rear E2R (m)" hint="Unequal-pitch only">
                  <input type="number" step="0.01" className="rqp mt-1.5" value={section.rear_eave_to_ridge ?? ""} onChange={(e) => setNum("rear_eave_to_ridge", e.target.value)} />
                </Field>
                <Field label="Wind zone" hint="BS 5534 Annex B">
                  <select className="rqp mt-1.5" value={section.wind_zone || ""} onChange={(e) => set("wind_zone", e.target.value || null)}>
                    <option value="">—</option>
                    {WIND_ZONES.map((z) => <option key={z} value={z}>{z}</option>)}
                  </select>
                </Field>
                <Field label="Exposure">
                  <select className="rqp mt-1.5" value={section.exposure || ""} onChange={(e) => set("exposure", e.target.value || null)}>
                    <option value="">—</option>
                    {EXPOSURE_OPTIONS.map((z) => <option key={z} value={z}>{z}</option>)}
                  </select>
                </Field>
                <Field label="Batten gauge (mm)" hint="Mfr / pitch dependent">
                  <input type="number" step="1" className="rqp mt-1.5" value={section.batten_gauge_mm ?? ""} onChange={(e) => setNum("batten_gauge_mm", e.target.value)} />
                </Field>
                <Field label="Fixing schedule">
                  <input className="rqp mt-1.5" value={section.fixing_spec || ""} placeholder="1 nail + 1 clip" onChange={(e) => set("fixing_spec", e.target.value || null)} />
                </Field>
                <Field label="Ventilation" hint="BS 5250">
                  <input className="rqp mt-1.5" value={section.ventilation_spec || ""} placeholder="10mm eaves + 5mm ridge" onChange={(e) => set("ventilation_spec", e.target.value || null)} />
                </Field>
                <Field label="Waste % override">
                  <input type="number" step="0.5" className="rqp mt-1.5" value={section.waste_pct_override ?? ""} onChange={(e) => setNum("waste_pct_override", e.target.value)} />
                </Field>

                {/* BS EN 1991-1-4 advanced — drives the fixing calculator */}
                <Field label="Building height (m)" hint="Ridge above ground">
                  <input type="number" step="0.1" className="rqp mt-1.5" value={section.building_height_m ?? ""} onChange={(e) => setNum("building_height_m", e.target.value)} data-testid={`section-building-height-${index}`} />
                </Field>
                <Field label="Terrain (EN 1991-1-4)" hint="0 sea · I open · II isolated · III suburban · IV urban">
                  <select className="rqp mt-1.5" value={section.terrain || ""} onChange={(e) => set("terrain", e.target.value || null)} data-testid={`section-terrain-${index}`}>
                    <option value="">— Auto —</option>
                    <option value="0">0 — Sea / coastal</option>
                    <option value="I">I — Lakes / open</option>
                    <option value="II">II — Low vegetation</option>
                    <option value="III">III — Suburban / industrial</option>
                    <option value="IV">IV — Urban (≥15 m density)</option>
                  </select>
                </Field>
                <Field label="Topography c_o(z)" hint="1.0 unless on a hill or escarpment">
                  <input type="number" step="0.01" className="rqp mt-1.5" value={section.topography_factor ?? ""} onChange={(e) => setNum("topography_factor", e.target.value)} />
                </Field>
                <Field label="Orography" hint="Hills / escarpments amplify Vb">
                  <select className="rqp mt-1.5" value={section.orography || ""} onChange={(e) => set("orography", e.target.value || null)}>
                    <option value="">— None —</option>
                    <option value="isolated_hill">Isolated hill</option>
                    <option value="ridge">Ridge</option>
                    <option value="cliff">Cliff / escarpment</option>
                  </select>
                </Field>
                <Field label="Safety factor" hint="BS 5534 default 1.5">
                  <input type="number" step="0.05" className="rqp mt-1.5" value={section.safety_factor ?? ""} onChange={(e) => setNum("safety_factor", e.target.value)} />
                </Field>

                <Field label="Ridge (m, auto if blank)">
                  <input type="number" step="0.01" className="rqp mt-1.5" value={section.ridge_length ?? ""} onChange={(e) => setNum("ridge_length", e.target.value)} />
                </Field>
                <Field label="Hip (m, auto if blank)">
                  <input type="number" step="0.01" className="rqp mt-1.5" value={section.hip_length ?? ""} onChange={(e) => setNum("hip_length", e.target.value)} />
                </Field>
                <Field label="Valley (m, auto if blank)">
                  <input type="number" step="0.01" className="rqp mt-1.5" value={section.valley_length ?? ""} onChange={(e) => setNum("valley_length", e.target.value)} />
                </Field>
                <Field label="Eaves (m, auto if blank)">
                  <input type="number" step="0.01" className="rqp mt-1.5" value={section.eaves_length ?? ""} onChange={(e) => setNum("eaves_length", e.target.value)} />
                </Field>
              </div>
            )}
          </div>

          {/* Per-section compliance warnings */}
          {sectionWarnings.length > 0 && (
            <div className="pt-3 border-t border-slate-200" data-testid={`section-warnings-list-${index}`}>
              <div className="overline mb-2">Compliance audit</div>
              <ul className="space-y-1.5">
                {sectionWarnings.map((w, wi) => (
                  <li key={`${w.rule || "rule"}-${wi}`} className={`text-xs flex items-start gap-2 p-2 rounded-sm border ${w.severity === "critical" ? "bg-red-50 border-red-200 text-red-800" : w.severity === "warning" ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-slate-50 border-slate-200 text-slate-700"}`}>
                    <Warning size={12} weight="fill" className="mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="font-semibold">{w.message}</div>
                      <div className="text-[10px] uppercase tracking-wider opacity-80 mt-0.5">
                        {w.standard}{w.clause ? ` · ${w.clause}` : ""}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
